import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * INVOICE
 *
 * Two shapes the invoice screens drew by hand, in two files each:
 *
 *   InvoicePreview        the document itself — the heading and company mark,
 *                         the dates, who it is billed to, the line items, the
 *                         totals and the notes. The create screen shows it in
 *                         a dialog; the generator rasterises it into the PDF.
 *   InvoiceTotalsPanel    the boxed totals under the line items: subtotal,
 *                         discount, tax, total. Both the create screen and the
 *                         read-only view render it.
 */

export const invoicePreviewRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex w-full flex-col gap-8'],
  {
    variants: {
      inset: {
        md: 'p-6',
        lg: 'p-12',
      },
      /**
       * A printed page. The generator rasterises this node into the PDF the
       * customer receives, so it stays light whatever theme the app is in —
       * following the theme would put a dark invoice in their inbox.
       */
      paper: {
        // design-tokens-ignore: print output; the app's theme must not reach it
        true: 'light rounded-lg border border-border bg-white text-foreground',
        false: '',
      },
    },
    defaultVariants: { inset: 'lg', paper: false },
  }
);

/** The company mark in the corner of the document. */
export const invoicePreviewLogoRecipe = cva([
  uiMarker,
  'mb-3 h-16 w-auto object-contain',
]);

/** The dates band: a rule above and below, set off from the heading. */
export const invoicePreviewDetailsRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'border-y border-border py-6',
]);

/** The totals column, kept narrow and pushed to the right. */
export const invoicePreviewTotalsRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'w-64',
]);

/** A section under a rule at the foot of the document: notes, the email. */
export const invoicePreviewSectionRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'border-t border-border pt-6',
]);

/**
 * The totals box. Clips its corners so the highlighted total row follows the
 * Surface's radius rather than poking out of it.
 */
export const invoiceTotalsPanelRecipe = cva([
  uiMarker,
  'w-full overflow-hidden',
]);

export type InvoicePreviewRecipeProps = VariantProps<
  typeof invoicePreviewRecipe
>;
